import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ServiceManager from 'services/ServiceManager';
import { useFormatting } from 'locale';
import AdapterError from 'errors/AdapterError';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import HandlerError, { AppError } from 'errors/HandlerError';
import { generateDFSClientId } from 'utils';
import { Portfolio } from 'core/types/api/wms';
import { getDocumentsPortfolios } from '../utils';
import { adaptPortfolios } from '../../Portfolios/adapters/adaptPortfolios';
import { portfolioActions } from '../../../constants';

// --- Interfaces ---

interface FilteredPortfolios {
    Portfolios: Portfolio[];
    [key: string]: unknown;
}

interface PortfolioOverview extends Record<string, unknown> {
    Id: string;
}

interface UsePortfoliosProps {
    clientId: string | number;
}

interface GetPortfoliosArgs {
    documents: unknown[];
}

interface UsePortfoliosReturn {
    getPortfolios: (args: GetPortfoliosArgs) => Promise<any>;
    isLoadingPortfolios: boolean;
    error: AppError | null;
}

export const usePortfolios = ({ clientId }: UsePortfoliosProps): UsePortfoliosReturn => {
    const [error, setError] = useState<AppError | null>(null);
    const [isLoadingPortfolios, setLoadingPortfolios] = useState<boolean>(false);
    const { i18n, t } = useTranslation();
    const { getFormattedCurrency } = useFormatting();

    const getPortfolios = useCallback(async ({ documents }: GetPortfoliosArgs) => {
        setError(null);
        setLoadingPortfolios(true);

        try {
            const params = { language: i18n.language };

            // Fetch basic portfolios
            const { data: allPortfolios } = await ServiceManager.portfolioManagement(
                'getPortfolios',
                [{ contactId: clientId, ...params }],
            );

            // Filter portfolios based on documents
            const filteredPortfolios = getDocumentsPortfolios({
                portfolios: allPortfolios,
                documents,
            }) as FilteredPortfolios;

            // Fetch overview details for each filtered portfolio
            const portfoliosOverview: PortfolioOverview[] = await Promise.all(
                filteredPortfolios.Portfolios.map(async ({ Id }) => {
                    const response = await ServiceManager.portfolioManagement(
                        'getPortfolioOverview',
                        [Id, clientId, params],
                    );

                    return {
                        Id,
                        ...response.data,
                    };
                }),
            );

            const dfsId = generateDFSClientId(clientId);

            try {
                setLoadingPortfolios(false);

                return adaptPortfolios({
                    data: filteredPortfolios,
                    dfsId,
                    portfolioActions,
                    t,
                    getFormattedCurrency,
                    portfoliosOverview,
                });
            } catch (err) {
                // Re-wrap error for the adapter failure
                throw new AdapterError(err instanceof Error ? err.message : String(err));
            }
        } catch (err) {
            handlerRequestCanceling(
                HandlerError({
                    setError,
                    setLoading: setLoadingPortfolios,
                }),
            )(err);

            return null;
        }
    }, [clientId, i18n.language, t, getFormattedCurrency]);

    return {
        getPortfolios,
        isLoadingPortfolios,
        error,
    };
};
